/**
 * Desk Browse's filters, read from and written to the hash (a4-09). One
 * import, `browse.ts` by its extension (itself loadable by Node), so the
 * Worker's suite still loads it (`test/deskRoute.test.ts`).
 *
 * The phone writes every Browse link — Today's "All N" (`?when=tonight`), a
 * person chip (`?person=`), a party's eyebrow (`?series=`), a place page
 * (`#/places/<id>`, which the router reads as `#/browse/<id>?mode=places`) —
 * and the desk used to keep its filters in component state and read none of
 * them. The route is now the filters' only home: the screen derives them from
 * it on every render and writes a change back with `go`, so a desk URL is one
 * a phone can open and the other way round.
 *
 * Where the two shells differ:
 * - `when`: one list of five windows, `browse.ts`'s `WINDOWS` (a4-14), and
 *   two defaults — no `when` is Next 30 days here and Any time on the phone
 *   (`DESK_WHEN`, `PHONE_WHEN`). `when=all`, the desk's old word for Any
 *   time, still reads as it.
 * - An entity filter (`person`, `ensemble`, `series`, a place) with no `when`
 *   opens on Any time: "everything by this conductor" is what the phone shows
 *   for that link, and Next 30 days would quietly drop most of it.
 * - A place: there is no desk Places screen, so a place link opens Browse
 *   narrowed to the rows that play in that building. `?mode=places` with no
 *   id is the phone's Places list, and lands on plain Browse.
 * - `district`, `free=1`, `en=1` are desk-only keys; the phone ignores them.
 * - `day=YYYY-MM-DD` (a3-20) is the desk Year's "see in Browse": the rows on
 *   that one Berlin day. With no `when` it opens on Any time, since the day
 *   is already the window and Next 30 days would empty a day in March.
 *   Anything not shaped like a date is ignored, as an unknown kind is.
 */

import { DESK_WHEN, whenOf, type When } from './browse.ts'

/** A window: `browse.ts`'s `When`, the one vocabulary both shells read. */
export type Win = When

/** The narrowing set an entity link asks for. At most one: the phone reads
 *  `person` before `ensemble` and a series page as a list of its own, so a
 *  hand-edited URL carrying two narrows by the first of them, here as there. */
export type Narrow =
  | { by: 'person'; id: string }
  | { by: 'ensemble'; id: string }
  | { by: 'series'; id: string }
  | { by: 'place'; id: string }

export interface DeskFilters {
  win: Win
  kind: string | null
  district: string
  free: boolean
  english: boolean
  q: string
  narrow: Narrow | null
  /** One Berlin day, `YYYY-MM-DD`, or null. */
  day: string | null
}

export const DESK_DEFAULTS: DeskFilters = {
  win: DESK_WHEN, kind: null, district: '', free: false, english: false, q: '', narrow: null, day: null,
}

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

/** The filters a Browse route asks for. `id` is the route's second segment:
 *  a kind (`#/browse/concert`) in Events, a building in Places mode. */
export function filtersFromRoute(
  id: string | null,
  query: Record<string, string>,
  kinds: readonly string[],
): DeskFilters {
  const places = query.mode === 'places'
  const narrow: Narrow | null =
    places ? (id ? { by: 'place', id } : null)
      : query.person ? { by: 'person', id: query.person }
        : query.ensemble ? { by: 'ensemble', id: query.ensemble }
          : query.series ? { by: 'series', id: query.series }
            : null
  const kindAsked = query.kind ?? (!places && id && id !== 'hidden' ? id : null)
  const kind = kindAsked && kinds.includes(kindAsked) ? kindAsked : null
  const day = query.day && DAY.test(query.day) ? query.day : null
  const win = whenOf(query.when) ?? (narrow || day ? 'any' : DESK_DEFAULTS.win)
  return {
    win, kind, narrow, day,
    district: query.district ?? '',
    free: query.free === '1',
    english: query.en === '1',
    q: query.q ?? '',
  }
}

/** The route that says these filters: `go('browse', id, query)`. Only what
 *  differs from what `filtersFromRoute` would read without it is written,
 *  so the default list is a bare `#/browse`. */
export function routeFromFilters(f: DeskFilters): { id: string | null; query: Record<string, string> } {
  const query: Record<string, string> = {}
  const n = f.narrow
  const fallback: Win = n || f.day ? 'any' : DESK_DEFAULTS.win
  if (f.win !== fallback) query.when = f.win
  if (f.kind) query.kind = f.kind
  if (f.q) query.q = f.q
  if (f.district) query.district = f.district
  if (f.free) query.free = '1'
  if (f.english) query.en = '1'
  if (f.day) query.day = f.day
  if (n && n.by !== 'place') query[n.by] = n.id
  if (n?.by === 'place') {
    query.mode = 'places'
    return { id: n.id, query }
  }
  return { id: null, query }
}

// ---------------------------------------------------------------------------
// Narrowing a row
// ---------------------------------------------------------------------------

/** The fields a narrowing reads, declared rather than imported. */
export interface NarrowRow {
  people?: { id: string }[] | null
  ensembles?: { id: string }[] | null
  series?: ({ id: string } | null)[] | null
  venue_id?: string | null
  building_id?: string | null
}
export interface NarrowShowing { venue_id?: string | null; building_id?: string | null }

/**
 * Does the row belong under this narrowing. Every array is `?? []`: a row
 * cached before `people` or `series` reached the view has no key at all
 * (CLAUDE.md, the E5 stale-cache trap).
 *
 * A place is placed the way `Places.placesOf` places a row: by the buildings
 * its showings play in, and by its own venue when it has no placed showing
 * (an exhibition run). `parentOf` turns a hall into its building, the
 * link's id included.
 */
export function narrows(
  row: NarrowRow,
  showings: readonly NarrowShowing[],
  n: Narrow,
  parentOf: (venueId: string) => string | null | undefined,
): boolean {
  if (n.by === 'person') return (row.people ?? []).some(p => p?.id === n.id)
  if (n.by === 'ensemble') return (row.ensembles ?? []).some(s => s?.id === n.id)
  if (n.by === 'series') return (row.series ?? []).some(s => s?.id === n.id)
  const building = (venue: string, own?: string | null) => own ?? parentOf(venue) ?? venue
  // A link to a hall means its building, which is the page the phone opens.
  const want = building(n.id)
  const placed = showings.filter(s => s.venue_id)
  if (placed.length) return placed.some(s => building(s.venue_id!, s.building_id) === want)
  return !!row.venue_id && building(row.venue_id, row.building_id) === want
}
